import React, { useState, useEffect, useCallback } from 'react';
import {
  LayoutDashboard, Shield, Database, Archive, Trash2, FileText, Calendar, Clock, AlertCircle, CheckCircle2, Settings, Plus, Edit, Save, RefreshCw, Download, Upload, Search, Filter, BarChart3, PieChart, LineChart, TrendingUp, TrendingDown, Users, Activity, Eye, EyeOff, Lock, Unlock, Key, Home, Menu, X, Check, XCircle, Info, HelpCircle, ExternalLink, Copy, Globe, Server, HardDrive, Cpu, Network, Mail, Phone, MessageSquare, Heart, Award, Target, Timer, Brain, Route, Lightning, MapPin, Play
} from 'lucide-react';
import { AdminService } from '../../services/AdminService';

const DataRetentionManagement = () => {
  const [retentionPolicies, setRetentionPolicies] = useState([]);
  const [archivingRules, setArchivingRules] = useState([]);
  const [deletionPolicies, setDeletionPolicies] = useState([]);
  const [retentionMetrics, setRetentionMetrics] = useState(null);
  const [complianceReports, setComplianceReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('30d');
  const [policyFilter, setPolicyFilter] = useState('all');

  const loadRetentionData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const policiesResponse = await new AdminService().getRetentionPolicies(timeRange, policyFilter);
      const archivingResponse = await new AdminService().getArchivingRules();
      const deletionResponse = await new AdminService().getDeletionPolicies();
      const metricsResponse = await new AdminService().getRetentionMetrics(timeRange);
      const reportsResponse = await new AdminService().getComplianceReports();

      if (policiesResponse.success && archivingResponse.success && deletionResponse.success && 
          metricsResponse.success && reportsResponse.success) {
        setRetentionPolicies(policiesResponse.data);
        setArchivingRules(archivingResponse.data);
        setDeletionPolicies(deletionResponse.data);
        setRetentionMetrics(metricsResponse.data);
        setComplianceReports(reportsResponse.data);
      } else {
        setError(policiesResponse.message || archivingResponse.message || deletionResponse.message || 
                 metricsResponse.message || reportsResponse.message);
      }
    } catch (err) {
      setError('Failed to fetch data retention information.');
      console.error('Error fetching retention data:', err);
    } finally {
      setLoading(false);
    }
  }, [timeRange, policyFilter]);

  useEffect(() => {
    loadRetentionData();
    const interval = setInterval(loadRetentionData, 60000); // Refresh every 60 seconds
    return () => clearInterval(interval);
  }, [loadRetentionData]);

  const handleCreatePolicy = async (policyType, policyData) => {
    if (window.confirm(`Are you sure you want to create a new ${policyType} policy?`)) {
      try {
        const response = await new AdminService().createRetentionPolicy(policyType, policyData);
        if (response.success) {
          alert(`${policyType} policy created successfully!`);
          loadRetentionData();
        } else {
          alert(`Failed to create policy: ${response.message}`);
        }
      } catch (err) {
        alert('Error creating policy.');
        console.error('Error creating policy:', err);
      }
    }
  };

  const handleUpdatePolicy = async (policyId, policyType, policyData) => {
    if (window.confirm(`Are you sure you want to update this ${policyType} policy?`)) {
      try {
        const response = await new AdminService().updateRetentionPolicy(policyId, policyType, policyData);
        if (response.success) {
          alert(`${policyType} policy updated successfully!`);
          loadRetentionData();
        } else {
          alert(`Failed to update policy: ${response.message}`);
        }
      } catch (err) {
        alert('Error updating policy.');
        console.error('Error updating policy:', err);
      }
    }
  };

  const handleDeletePolicy = async (policyId, policyType) => {
    if (window.confirm(`Are you sure you want to delete this ${policyType} policy? This action cannot be undone.`)) {
      try {
        const response = await new AdminService().deleteRetentionPolicy(policyId, policyType);
        if (response.success) {
          alert(`${policyType} policy deleted successfully!`);
          loadRetentionData();
        } else {
          alert(`Failed to delete policy: ${response.message}`);
        }
      } catch (err) {
        alert('Error deleting policy.');
        console.error('Error deleting policy:', err);
      }
    }
  };

  const handleExecuteRetention = async (policyId, policyType) => {
    if (window.confirm(`Are you sure you want to execute this ${policyType} policy? This will process data according to the policy rules.`)) {
      try {
        const response = await new AdminService().executeRetentionPolicy(policyId, policyType);
        if (response.success) {
          alert(`${policyType} policy executed successfully! ${response.data.processedRecords} records processed.`);
          loadRetentionData();
        } else {
          alert(`Failed to execute policy: ${response.message}`);
        }
      } catch (err) {
        alert('Error executing policy.');
        console.error('Error executing policy:', err);
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-600 bg-green-100';
      case 'inactive': return 'text-gray-600 bg-gray-100';
      case 'warning': return 'text-yellow-600 bg-yellow-100';
      case 'error': return 'text-red-600 bg-red-100';
      case 'pending': return 'text-orange-600 bg-orange-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'low': return 'text-blue-600 bg-blue-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'high': return 'text-orange-600 bg-orange-100';
      case 'critical': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  if (loading) {
    return <div className="p-6 text-center text-gray-500">Loading data retention information...</div>;
  }

  if (error) {
    return <div className="p-6 text-center text-red-500">Error: {error}</div>;
  }

  if (!retentionMetrics) {
    return <div className="p-6 text-center text-gray-500">No data retention information available.</div>;
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-800 flex items-center">
          <Database className="mr-3 text-blue-600" size={30} />
          Data Retention Management
        </h1>
        <div className="flex items-center space-x-4">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="p-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="90d">Last 90 Days</option>
            <option value="1y">Last Year</option>
          </select>
          <select
            value={policyFilter}
            onChange={(e) => setPolicyFilter(e.target.value)}
            className="p-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Policies</option>
            <option value="retention">Retention</option>
            <option value="archiving">Archiving</option>
            <option value="deletion">Deletion</option>
          </select>
          <button
            onClick={loadRetentionData}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md shadow-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <RefreshCw className="mr-2" size={18} /> Refresh
          </button>
        </div>
      </div>

      <p className="text-gray-600 mb-8">
        Manage data retention policies, archiving rules, and deletion policies to ensure compliance with data protection regulations.
        <span className="ml-2 text-sm text-gray-500">Last Updated: {new Date(retentionMetrics.lastUpdated).toLocaleString()}</span>
      </p>

      {/* Retention Overview Metrics */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-700 mb-4 flex items-center">
          <LayoutDashboard className="mr-2 text-purple-600" size={24} /> Retention Overview
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {retentionMetrics.overviewMetrics.map((metric, index) => (
            <div key={index} className="bg-white p-6 rounded-lg shadow-md border border-gray-200 flex items-center justify-between">
              <div>
                <p className="text-lg font-medium text-gray-600">{metric.name}</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{metric.value}</p>
                <div className="flex items-center mt-2 text-sm">
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getSeverityColor(metric.severity)}`}>
                    {metric.severity}
                  </span>
                  <span className="ml-3 text-gray-500">{metric.description}</span>
                </div>
              </div>
              {metric.type === 'active_policies' && <Shield size={40} className="text-blue-500 opacity-70" />}
              {metric.type === 'archived_records' && <Archive size={40} className="text-green-500 opacity-70" />}
              {metric.type === 'deleted_records' && <Trash2 size={40} className="text-red-500 opacity-70" />}
              {metric.type === 'compliance_score' && <CheckCircle2 size={40} className="text-purple-500 opacity-70" />}
            </div>
          ))}
        </div>
      </section>

      {/* Retention Policies */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-700 mb-4 flex items-center">
          <Shield className="mr-2 text-green-600" size={24} /> Retention Policies
        </h2>
        <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-200 flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">Data Retention Configuration</h3>
            <button
              onClick={() => handleCreatePolicy('retention', {})}
              className="flex items-center px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
            >
              <Plus className="mr-2" size={16} /> Add Policy
            </button>
          </div>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Policy Name
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Data Type
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Retention Period
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Records Affected
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {retentionPolicies.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-4 text-center text-gray-500">No retention policies configured.</td>
                </tr>
              ) : (
                retentionPolicies.map((policy) => (
                  <tr key={policy.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{policy.name}</div>
                      <div className="text-sm text-gray-500">{policy.description}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                        {policy.dataType}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{policy.retentionPeriod}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(policy.status)}`}>
                        {policy.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{policy.recordsAffected}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => handleExecuteRetention(policy.id, 'retention')}
                        className="text-blue-600 hover:text-blue-900 mr-3"
                        title="Execute Policy"
                      >
                        <Play size={18} />
                      </button>
                      <button
                        onClick={() => handleUpdatePolicy(policy.id, 'retention', policy)}
                        className="text-green-600 hover:text-green-900 mr-3"
                        title="Edit Policy"
                      >
                        <Edit size={18} />
                      </button>
                      <button
                        onClick={() => handleDeletePolicy(policy.id, 'retention')}
                        className="text-red-600 hover:text-red-900"
                        title="Delete Policy"
                      >
                        <Trash2 size={18} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Archiving Rules */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-700 mb-4 flex items-center">
          <Archive className="mr-2 text-orange-600" size={24} /> Archiving Rules
        </h2>
        <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-200 flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">Data Archiving Configuration</h3>
            <button
              onClick={() => handleCreatePolicy('archiving', {})}
              className="flex items-center px-3 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700"
               style={{ background: 'linear-gradient(135deg,#006600 0%,#BB0000 100%)' }}
            >
              <Plus className="mr-2" size={16} /> Add Rule
            </button>
          </div>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rule Name
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Archive Trigger
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Storage Location
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Executed
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {archivingRules.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-4 text-center text-gray-500">No archiving rules configured.</td>
                </tr>
              ) : (
                archivingRules.map((rule) => (
                  <tr key={rule.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{rule.name}</div>
                      <div className="text-sm text-gray-500">{rule.description}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{rule.archiveTrigger}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{rule.storageLocation}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(rule.status)}`}>
                        {rule.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(rule.lastExecuted).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => handleExecuteRetention(rule.id, 'archiving')}
                        className="text-blue-600 hover:text-blue-900 mr-3"
                        title="Execute Rule"
                      >
                        <Play size={18} />
                      </button>
                      <button
                        onClick={() => handleUpdatePolicy(rule.id, 'archiving', rule)}
                        className="text-green-600 hover:text-green-900 mr-3"
                        title="Edit Rule"
                      >
                        <Edit size={18} />
                      </button>
                      <button
                        onClick={() => handleDeletePolicy(rule.id, 'archiving')}
                        className="text-red-600 hover:text-red-900"
                        title="Delete Rule"
                      >
                        <Trash2 size={18} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Deletion Policies */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-700 mb-4 flex items-center">
          <Trash2 className="mr-2 text-red-600" size={24} /> Deletion Policies
        </h2>
        <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-200 flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">Data Deletion Configuration</h3>
            <button
              onClick={() => handleCreatePolicy('deletion', {})}
              className="flex items-center px-3 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
            >
              <Plus className="mr-2" size={16} /> Add Policy
            </button>
          </div>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Policy Name
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Deletion Criteria
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Safety Period
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Records Deleted
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {deletionPolicies.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-4 text-center text-gray-500">No deletion policies configured.</td>
                </tr>
              ) : (
                deletionPolicies.map((policy) => (
                  <tr key={policy.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{policy.name}</div>
                      <div className="text-sm text-gray-500">{policy.description}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{policy.deletionCriteria}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{policy.safetyPeriod}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(policy.status)}`}>
                        {policy.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{policy.recordsDeleted}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => handleExecuteRetention(policy.id, 'deletion')}
                        className="text-blue-600 hover:text-blue-900 mr-3"
                        title="Execute Policy"
                      >
                        <Play size={18} />
                      </button>
                      <button
                        onClick={() => handleUpdatePolicy(policy.id, 'deletion', policy)}
                        className="text-green-600 hover:text-green-900 mr-3"
                        title="Edit Policy"
                      >
                        <Edit size={18} />
                      </button>
                      <button
                        onClick={() => handleDeletePolicy(policy.id, 'deletion')}
                        className="text-red-600 hover:text-red-900"
                        title="Delete Policy"
                      >
                        <Trash2 size={18} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Compliance Reports */}
      <section>
        <h2 className="text-2xl font-semibold text-gray-700 mb-4 flex items-center">
          <FileText className="mr-2 text-indigo-600" size={24} /> Compliance Reports
        </h2>
        <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-200 flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">Data Protection Compliance</h3>
            <button
              onClick={() => window.alert('Compliance report generation initiated!')}
              className="flex items-center px-3 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
            >
              <Download className="mr-2" size={16} /> Generate Report
            </button>
          </div>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Report Name
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Generated
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {complianceReports.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-4 text-center text-gray-500">No compliance reports available.</td>
                </tr>
              ) : (
                complianceReports.map((report) => (
                  <tr key={report.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{report.name}</div>
                      <div className="text-sm text-gray-500">{report.description}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                        {report.type}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(report.generated).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(report.status)}`}>
                        {report.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => window.alert('Report downloaded successfully!')}
                        className="text-blue-600 hover:text-blue-900 mr-3"
                        title="Download Report"
                      >
                        <Download size={18} />
                      </button>
                      <button
                        onClick={() => window.alert('Report preview opened!')}
                        className="text-green-600 hover:text-green-900"
                        title="Preview Report"
                      >
                        <Eye size={18} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default DataRetentionManagement;